import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Users, LayoutGrid, CheckCircle, ChevronRight, ChevronDown,
  Sparkles, FileText, Target, Plus, X, Info, ArrowRight
} from 'lucide-react';
import { sessionAPI, projectsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { listAvailableRooms, Room } from '../services/rooms';
import {
  browseAvatars,
  getAvatarGroups,
} from '../services/avatarCatalogService';
import {
  AvatarDefinition,
  AvatarGroup,
  AvatarScope,
  AvatarCategory,
  SessionTemplate
} from '../types';
import { useTemplates } from '../hooks/useTemplates';
import { createSessionFromTemplate } from '../services/templateService';
import TemplatePreview from '../components/template/TemplatePreview';

const WORKSHOP_PHASES = [
  { value: 'intros', label: 'Introductions' },
  { value: 'challenge_deep_dive', label: 'Challenge Deep Dive' },
  { value: 'mini_prfaq', label: 'Mini PR/FAQ' },
  { value: 'persona_identification', label: 'Persona Identification' },
  { value: 'user_journeys', label: 'User Journeys' },
  { value: 'assumptions_constraints', label: 'Assumptions & Constraints' },
  { value: 'next_steps', label: 'Next Steps' }
];

type AvatarSelectionMode = 'default' | 'group' | 'custom';

interface SelectedAvatar {
  avatarDefinitionId: string;
  versionNumber: number;
  slotNumber: number;
  name: string;
  category: AvatarCategory;
  supportsPhysicalDevice: boolean;
}

interface ResolvedAvatarSelection {
  avatarGroupId: string | undefined;
  customAvatars: Array<{ avatarDefinitionId: string; versionNumber: number; slotNumber: number }> | undefined;
}

function resolveAvatarSelection(
  avatarMode: AvatarSelectionMode,
  selectedGroupId: string,
  selectedAvatars: SelectedAvatar[]
): ResolvedAvatarSelection {
  let avatarGroupId: string | undefined;
  let customAvatars: ResolvedAvatarSelection['customAvatars'];

  if (avatarMode === 'group' && selectedGroupId) {
    avatarGroupId = selectedGroupId;
  } else if (avatarMode === 'custom' && selectedAvatars.length > 0) {
    customAvatars = selectedAvatars.map(a => ({
      avatarDefinitionId: a.avatarDefinitionId,
      versionNumber: a.versionNumber,
      slotNumber: a.slotNumber
    }));
  }

  return { avatarGroupId, customAvatars };
}

interface CreateSessionRequestParams {
  selectedTemplateId: string;
  selectedTemplate: SessionTemplate | undefined;
  userId: string;
  customerName: string;
  goals: string[];
  avatarSelection: ResolvedAvatarSelection;
  projectId: string;
  roomId: string;
  enabledPhases: string[];
}

async function buildCreateSessionRequest(params: CreateSessionRequestParams) {
  const {
    selectedTemplateId, selectedTemplate, userId, customerName, goals,
    avatarSelection, projectId, roomId, enabledPhases
  } = params;
  const { avatarGroupId, customAvatars } = avatarSelection;

  if (selectedTemplateId && selectedTemplate) {
    return createSessionFromTemplate({
      templateId: selectedTemplateId,
      templateVersion: selectedTemplate.versionNumber,
      customerId: userId,
      customerName,
      goals,
      avatarGroupId,
      customAvatars,
      projectId: projectId || undefined,
      roomId: roomId || undefined
    });
  }

  if (enabledPhases.length === 0) {
    throw new Error('At least one phase must be enabled');
  }

  return sessionAPI.createSession({
    customerName,
    goals,
    enabledPhases,
    avatarGroupId,
    customAvatars,
    projectId: projectId || undefined,
    roomId: roomId || undefined
  });
}

export default function SessionCreator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { templates, phases, loadSessionTemplates, loadPhaseTemplates } = useTemplates();

  const sessionTemplates = templates;
  const phaseTemplates = phases;
  const defaultTemplate = templates.find(t => t.isDefault);

  const [formData, setFormData] = useState({
    customerName: '',
    goals: [''],
    enabledPhases: WORKSHOP_PHASES.map(p => p.value)
  });
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [loadingProjects, setLoadingProjects] = useState(false);

  const [availableRooms, setAvailableRooms] = useState<Room[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<string>('');
  const [loadingRooms, setLoadingRooms] = useState(false);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [useTemplatePhases, setUseTemplatePhases] = useState(true);

  const [avatarMode, setAvatarMode] = useState<AvatarSelectionMode>('default');
  const [avatarGroups, setAvatarGroups] = useState<AvatarGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [availableAvatars, setAvailableAvatars] = useState<AvatarDefinition[]>([]);
  const [selectedAvatars, setSelectedAvatars] = useState<SelectedAvatar[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingAvatars, setLoadingAvatars] = useState(false);

  const organizationId = user?.organizationId || '';
  const selectedTemplate = sessionTemplates.find((t: SessionTemplate) => t.sessionTemplateId === selectedTemplateId);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        await loadSessionTemplates();
        await loadPhaseTemplates();
      } catch (err) {
        console.error('Failed to load templates:', err);
      }
    };
    loadTemplates();
  }, [loadSessionTemplates, loadPhaseTemplates]);

  const loadAvatarGroups = useCallback(async () => {
    try {
      const result = await getAvatarGroups(organizationId);
      setAvatarGroups(result.groups);
    } catch (err) {
      console.error('Failed to load avatar groups:', err);
    }
  }, [organizationId]);

  useEffect(() => { loadAvatarGroups(); }, [loadAvatarGroups]);
  useEffect(() => { loadProjects(); loadRooms(); }, []);

  const loadProjects = async () => {
    try {
      setLoadingProjects(true);
      const result = await projectsAPI.listProjects();
      setProjects(result.projects || []);
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadRooms = async () => {
    try {
      setLoadingRooms(true);
      const rooms = await listAvailableRooms();
      setAvailableRooms(rooms);
    } catch (err) {
      console.error('Failed to load rooms:', err);
    } finally {
      setLoadingRooms(false);
    }
  };

  useEffect(() => {
    if (avatarMode === 'custom') {
      loadAvailableAvatars();
    }
  }, [avatarMode]);

  const loadAvailableAvatars = async () => {
    try {
      setLoadingAvatars(true);
      const result = await browseAvatars({ scope: AvatarScope.ORGANIZATION });
      const latestVersionsMap = new Map<string, AvatarDefinition>();
      result.avatars.forEach(avatar => {
        const existing = latestVersionsMap.get(avatar.avatarDefinitionId);
        if (!existing || avatar.versionNumber > existing.versionNumber) {
          latestVersionsMap.set(avatar.avatarDefinitionId, avatar);
        }
      });
      setAvailableAvatars(Array.from(latestVersionsMap.values()));
    } catch (err) {
      console.error('Failed to load avatars:', err);
    } finally {
      setLoadingAvatars(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGoalChange = (index: number, value: string) => {
    const newGoals = [...formData.goals];
    newGoals[index] = value;
    setFormData(prev => ({ ...prev, goals: newGoals }));
  };

  const addGoal = () => {
    setFormData(prev => ({ ...prev, goals: [...prev.goals, ''] }));
  };

  const removeGoal = (index: number) => {
    if (formData.goals.length > 1) {
      const newGoals = formData.goals.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, goals: newGoals }));
    }
  };

  const togglePhase = (phaseValue: string) => {
    setFormData(prev => {
      const isEnabled = prev.enabledPhases.includes(phaseValue);
      const newPhases = isEnabled
        ? prev.enabledPhases.filter(p => p !== phaseValue)
        : [...prev.enabledPhases, phaseValue];
      return { ...prev, enabledPhases: newPhases };
    });
  };

  const handleAvatarModeChange = (mode: AvatarSelectionMode) => {
    setAvatarMode(mode);
    setSelectedGroupId('');
    setSelectedAvatars([]);
  };

  const handleAvatarToggle = (avatar: AvatarDefinition) => {
    const isSelected = selectedAvatars.some(a => a.avatarDefinitionId === avatar.avatarDefinitionId);
    if (isSelected) {
      setSelectedAvatars(prev => prev.filter(a => a.avatarDefinitionId !== avatar.avatarDefinitionId));
    } else {
      const newSlot = selectedAvatars.length + 1;
      setSelectedAvatars(prev => [...prev, {
        avatarDefinitionId: avatar.avatarDefinitionId,
        versionNumber: avatar.versionNumber,
        slotNumber: newSlot,
        name: avatar.name,
        category: avatar.category,
        supportsPhysicalDevice: avatar.supportsPhysicalDevice
      }]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsCreating(true);

    try {
      const goals = formData.goals.filter(g => g.trim() !== '');
      if (!formData.customerName.trim()) {
        throw new Error('Customer name is required');
      }

      const avatarSelection = resolveAvatarSelection(avatarMode, selectedGroupId, selectedAvatars);

      const response = await buildCreateSessionRequest({
        selectedTemplateId,
        selectedTemplate,
        userId: user?.userId || '',
        customerName: formData.customerName,
        goals,
        avatarSelection,
        projectId: selectedProjectId,
        roomId: selectedRoomId,
        enabledPhases: formData.enabledPhases
      });

      navigate(`/session/${response.sessionId}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create session';
      setError(message);
      console.error('Session creation error:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const filteredAvatars = availableAvatars.filter(avatar =>
    avatar.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    avatar.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    avatar.keywords.some(k => k.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getCategoryColor = (category: AvatarCategory) => {
    const colors: Record<AvatarCategory, string> = {
      [AvatarCategory.FACILITATOR]: 'bg-indigo-500',
      [AvatarCategory.DOMAIN_EXPERT]: 'bg-blue-500',
      [AvatarCategory.STAKEHOLDER]: 'bg-emerald-500',
      [AvatarCategory.RESEARCHER]: 'bg-cyan-500',
      [AvatarCategory.CUSTOM]: 'bg-violet-500'
    };
    return colors[category] || 'bg-slate-500';
  };

  // Auto-select default template on first load
  useEffect(() => {
    if (defaultTemplate && !selectedTemplateId) {
      setSelectedTemplateId(defaultTemplate.sessionTemplateId);
    }
  }, [defaultTemplate, selectedTemplateId]);

  return (
    <div className="flex-1 overflow-y-auto relative flex flex-col bg-background-light min-h-screen">
      <div className="flex-1 w-full max-w-[860px] mx-auto px-6 py-8 pb-24">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-text-sub mb-6">
          <button type="button" className="hover:text-primary transition-colors cursor-pointer bg-transparent border-none p-0 text-inherit text-sm" onClick={() => navigate('/')}>Home</button>
          <ChevronRight size={16} />
          <button type="button" className="hover:text-primary transition-colors cursor-pointer bg-transparent border-none p-0 text-inherit text-sm" onClick={() => navigate('/')}>Sessions</button>
          <ChevronRight size={16} />
          <span className="text-text-main font-medium">New</span>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-main mb-2">Create New Session</h1>
          <p className="text-text-sub">Configure your workshop settings below to get started with your co-creation session.</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {/* Card 1: Session Details */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FileText size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-main">Session Details</h3>
                <p className="text-sm text-text-sub">Basic information about the session.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <label className="flex flex-col gap-2">
                <span className="text-sm font-medium text-text-main">Session Name <span className="text-red-500">*</span></span>
                <input
                  className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm h-11"
                  placeholder="Enter session name"
                  type="text"
                  value={formData.customerName}
                  onChange={(e) => handleInputChange('customerName', e.target.value)}
                  required
                />
              </label>
              {projects.length > 0 && (
                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-text-main">Link to Project</span>
                  <select
                    className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm h-11"
                    value={selectedProjectId}
                    onChange={(e) => setSelectedProjectId(e.target.value)}
                    disabled={loadingProjects}
                  >
                    <option value="">Select a project...</option>
                    {projects.map((project: any) => (
                      <option key={project.projectId} value={project.projectId}>
                        {project.name} ({project.sessionCount} sessions, {project.documentCount} docs)
                      </option>
                    ))}
                  </select>
                  <span className="text-xs text-text-sub">Associate this session with an existing project.</span>
                </label>
              )}
              <label className="flex flex-col gap-2">
                <span className="text-sm font-medium text-text-main">Room</span>
                <select
                  className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm h-11"
                  value={selectedRoomId}
                  onChange={(e) => setSelectedRoomId(e.target.value)}
                  disabled={loadingRooms}
                >
                  <option value="">Remote (no physical room)</option>
                  {availableRooms.map((room) => (
                    <option key={room.roomId} value={room.roomId}>
                      {room.name} ({room.locationId})
                    </option>
                  ))}
                </select>
                <span className="text-xs text-text-sub">Select a room to scope device access. Only devices in the selected room will be available.</span>
              </label>
            </div>
          </div>

          {/* Card 2: Session Template */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary/10 rounded-lg">
                <LayoutGrid size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-main">Select Template</h3>
                <p className="text-sm text-text-sub">Choose a starting point for your workshop.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* No template option */}
              <label className="cursor-pointer relative group">
                <input
                  className="peer sr-only"
                  name="template"
                  type="radio"
                  checked={selectedTemplateId === ''}
                  onChange={() => { setSelectedTemplateId(''); setUseTemplatePhases(true); }}
                />
                <div className="h-full p-4 rounded-xl border border-slate-200 hover:border-primary/50 bg-white peer-checked:border-2 peer-checked:border-primary peer-checked:bg-primary/5 transition-all">
                  <h4 className="font-semibold text-text-main mb-1">Custom Phases</h4>
                  <p className="text-xs text-text-sub leading-relaxed">Manually select workshop phases without a template.</p>
                </div>
              </label>

              {/* Default template */}
              {defaultTemplate && (
                <label className="cursor-pointer relative group">
                  <input
                    className="peer sr-only"
                    name="template"
                    type="radio"
                    checked={selectedTemplateId === defaultTemplate.sessionTemplateId}
                    onChange={() => { setSelectedTemplateId(defaultTemplate.sessionTemplateId); setUseTemplatePhases(true); }}
                  />
                  <div className="h-full p-4 rounded-xl border border-slate-200 hover:border-primary/50 bg-white peer-checked:border-2 peer-checked:border-primary peer-checked:bg-primary/5 transition-all">
                    <div className="flex justify-between items-start mb-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary text-white">Default</span>
                      {selectedTemplateId === defaultTemplate.sessionTemplateId && (
                        <CheckCircle size={18} className="text-primary" />
                      )}
                    </div>
                    <h4 className="font-semibold text-text-main mb-1">{defaultTemplate.name}</h4>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-medium text-text-sub bg-slate-100 px-1.5 py-0.5 rounded">v{defaultTemplate.versionNumber}</span>
                      <span className="text-xs text-text-sub">{defaultTemplate.phaseReferences.length} phases</span>
                    </div>
                    <p className="text-xs text-text-sub leading-relaxed">{defaultTemplate.description}</p>
                  </div>
                </label>
              )}

              {/* Other templates */}
              {sessionTemplates
                .filter((t: SessionTemplate) => t.sessionTemplateId !== defaultTemplate?.sessionTemplateId)
                .map((template: SessionTemplate) => (
                  <label key={template.sessionTemplateId} className="cursor-pointer relative group">
                    <input
                      className="peer sr-only"
                      name="template"
                      type="radio"
                      checked={selectedTemplateId === template.sessionTemplateId}
                      onChange={() => { setSelectedTemplateId(template.sessionTemplateId); setUseTemplatePhases(true); }}
                    />
                    <div className="h-full p-4 rounded-xl border border-slate-200 hover:border-primary/50 bg-white peer-checked:border-2 peer-checked:border-primary peer-checked:bg-primary/5 transition-all">
                      <div className="flex justify-between items-start mb-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">{template.category}</span>
                        {selectedTemplateId === template.sessionTemplateId && (
                          <CheckCircle size={18} className="text-primary" />
                        )}
                      </div>
                      <h4 className="font-semibold text-text-main mb-1">{template.name}</h4>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-medium text-text-sub bg-slate-100 px-1.5 py-0.5 rounded">v{template.versionNumber}</span>
                        <span className="text-xs text-text-sub">{template.phaseReferences.length} phases</span>
                      </div>
                      <p className="text-xs text-text-sub leading-relaxed">{template.description}</p>
                    </div>
                  </label>
                ))
              }
            </div>

            {/* Template Preview */}
            {selectedTemplate && (
              <div className="mt-6">
                <TemplatePreview
                  template={selectedTemplate}
                  phaseTemplates={phaseTemplates}
                />
              </div>
            )}

            {/* Advanced Options */}
            {selectedTemplate && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg cursor-pointer flex items-center justify-between text-sm font-semibold text-text-main hover:bg-slate-50 transition-colors"
                >
                  <span>Advanced Options</span>
                  {showAdvancedOptions ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {showAdvancedOptions && (
                  <div className="mt-3 p-4 bg-white border border-slate-200 rounded-lg">
                    <label className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useTemplatePhases}
                        onChange={(e) => setUseTemplatePhases(e.target.checked)}
                        className="cursor-pointer w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
                      />
                      <span className="text-amber-800 text-sm font-medium">Use template's phases (recommended)</span>
                    </label>
                    <p className="text-slate-500 text-xs mt-2 leading-relaxed">
                      Uncheck to manually select phases below. Note: This may break the template's workflow.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Card 3: Avatar Configuration */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Users size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-main">Avatar Configuration</h3>
                <p className="text-sm text-text-sub">Set up the AI facilitators for this session.</p>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              {/* Tabs */}
              <div className="bg-slate-100 p-1 rounded-lg flex text-sm font-medium">
                <button
                  type="button"
                  onClick={() => handleAvatarModeChange('default')}
                  className={`flex-1 py-2 px-4 rounded-md transition-all ${
                    avatarMode === 'default'
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Default Avatars
                </button>
                <button
                  type="button"
                  onClick={() => handleAvatarModeChange('group')}
                  className={`flex-1 py-2 px-4 rounded-md transition-all ${
                    avatarMode === 'group'
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Avatar Group
                </button>
                <button
                  type="button"
                  onClick={() => handleAvatarModeChange('custom')}
                  className={`flex-1 py-2 px-4 rounded-md transition-all ${
                    avatarMode === 'custom'
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Custom Selection
                </button>
              </div>

              {/* Default Mode */}
              {avatarMode === 'default' && (
                <div className="flex items-start gap-3 bg-primary/10 p-4 rounded-lg border border-primary/20">
                  <Info size={18} className="text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-primary/90">The default set of 7 facilitator avatars will be used for this session. Each phase will have an assigned specialist.</p>
                </div>
              )}

              {/* Group Mode */}
              {avatarMode === 'group' && (
                <div>
                  <label className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-text-main">Select Avatar Group</span>
                    <select
                      className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm h-11"
                      value={selectedGroupId}
                      onChange={(e) => setSelectedGroupId(e.target.value)}
                    >
                      <option value="">-- Select a group --</option>
                      {avatarGroups.map(group => (
                        <option key={group.avatarGroupId} value={group.avatarGroupId}>
                          {group.name} ({group.avatarSlots.length} avatars)
                        </option>
                      ))}
                    </select>
                  </label>
                  {avatarGroups.length === 0 && (
                    <p className="text-slate-500 text-sm mt-2">
                      No avatar groups available. Create one in the{' '}
                      <a href="/avatars" className="text-primary underline">Avatar Catalog</a>.
                    </p>
                  )}
                </div>
              )}

              {/* Custom Mode */}
              {avatarMode === 'custom' && (
                <div className="flex flex-col gap-4">
                  {/* Search */}
                  <div className="relative">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm h-11 pl-10"
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search avatars by name, description, or keywords..."
                    />
                  </div>

                  {/* Selected Avatars Preview */}
                  {selectedAvatars.length > 0 && (
                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <h4 className="text-sm font-semibold text-emerald-800 mb-2">
                        Selected Avatars ({selectedAvatars.length})
                      </h4>
                      <div className="flex gap-2 flex-wrap">
                        {selectedAvatars.map(avatar => (
                          <div
                            key={avatar.avatarDefinitionId}
                            className="px-3 py-1.5 bg-white border border-emerald-300 rounded-md text-sm flex items-center gap-1.5"
                          >
                            <span className="font-semibold text-text-main">{avatar.name}</span>
                            {avatar.supportsPhysicalDevice && <span title="Supports physical device">📱</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Avatar List */}
                  {loadingAvatars ? (
                    <div className="text-center py-10 text-slate-500">Loading avatars...</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
                      {filteredAvatars.map(avatar => {
                        const isSelected = selectedAvatars.some(a => a.avatarDefinitionId === avatar.avatarDefinitionId);
                        return (
                          <div
                            key={avatar.avatarDefinitionId}
                            onClick={() => handleAvatarToggle(avatar)}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                              isSelected
                                ? 'bg-emerald-50 border-emerald-400'
                                : 'bg-white border-slate-200 hover:border-primary/50'
                            }`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="text-sm font-semibold text-text-main">
                                {avatar.name} <span className="text-text-sub font-normal">v{avatar.versionNumber}</span>
                              </h4>
                              <span className={`px-2 py-0.5 ${getCategoryColor(avatar.category)} text-white rounded text-xs font-semibold`}>
                                {avatar.category}
                              </span>
                            </div>
                            <p className="text-xs text-text-sub leading-relaxed mb-2 line-clamp-2">
                              {avatar.description}
                            </p>
                            <div className="flex gap-1.5 items-center">
                              {avatar.supportsPhysicalDevice && (
                                <span className="px-2 py-0.5 bg-sky-50 text-sky-700 rounded text-xs font-semibold">
                                  📱 Physical Device
                                </span>
                              )}
                              {isSelected && (
                                <span className="px-2 py-0.5 bg-emerald-500 text-white rounded text-xs font-semibold">
                                  ✓ Selected
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {filteredAvatars.length === 0 && !loadingAvatars && (
                    <div className="text-center py-10 bg-white rounded-lg border-2 border-dashed border-slate-200 text-slate-500">
                      No avatars found matching your search.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Card 4: Workshop Goals */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Target size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-main">Workshop Goals</h3>
                <p className="text-sm text-text-sub">Define the key objectives for the AI to focus on.</p>
              </div>
            </div>
            <div className="space-y-3">
              {formData.goals.map((goal, index) => (
                <div
                  key={index} // NOSONAR: goals are plain mutable strings with no stable unique field
                  className="flex gap-2 items-center">
                  <span className="text-slate-400 text-sm font-medium w-6">{index + 1}.</span>
                  <input
                    className="flex-1 rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm h-11"
                    type="text"
                    value={goal}
                    onChange={(e) => handleGoalChange(index, e.target.value)}
                    placeholder={`Goal ${index + 1}`}
                  />
                  {formData.goals.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeGoal(index)}
                      className="text-slate-400 hover:text-red-500 transition-colors p-2"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addGoal}
                className="flex items-center gap-2 text-primary hover:text-primary/80 font-medium text-sm mt-2 px-2 py-1 rounded transition-colors w-fit"
              >
                <Plus size={18} />
                Add Goal
              </button>
            </div>
          </div>

          {/* Enabled Phases (only when no template or user unchecked useTemplatePhases) */}
          {(!selectedTemplate || !useTemplatePhases) && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Sparkles size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-main">Workshop Phases</h3>
                  <p className="text-sm text-text-sub">
                    {selectedTemplate ? 'Custom phase selection (overrides template)' : 'Select which phases to include in this workshop'}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {WORKSHOP_PHASES.map(phase => {
                  const isEnabled = formData.enabledPhases.includes(phase.value);
                  return (
                    <label
                      key={phase.value}
                      className={`flex items-center gap-3 p-3.5 rounded-lg border cursor-pointer transition-all ${
                        isEnabled
                          ? 'border-primary bg-primary/5'
                          : 'border-slate-200 hover:border-primary/50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={() => togglePhase(phase.value)}
                        className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                      />
                      <span className={`text-sm font-medium ${isEnabled ? 'text-text-main' : 'text-slate-500'}`}>
                        {phase.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Info message when using template phases */}
          {selectedTemplate && useTemplatePhases && (
            <div className="flex items-center gap-3 bg-emerald-50 p-4 rounded-xl border border-emerald-200">
              <CheckCircle size={20} className="text-emerald-700 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-emerald-800">Using Template Phases</h4>
                <p className="text-emerald-700 text-sm">
                  This session will use the {selectedTemplate.phaseReferences.length} phases defined in the "{selectedTemplate.name}" template.
                  Enable Advanced Options above to customize phases.
                </p>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="p-4 bg-red-50 text-red-600 rounded-lg border border-red-200">
              <strong>Error:</strong> {error}
            </div>
          )}
        </form>
      </div>

      {/* Bottom Action Bar */}
      <div className="sticky bottom-0 left-0 right-0 bg-white/80 backdrop-blur-md border-t border-slate-200 p-4 z-10">
        <div className="max-w-[860px] mx-auto flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/')}
            disabled={isCreating}
            className="px-6 py-2.5 rounded-lg border border-slate-300 text-text-main hover:bg-slate-50 font-medium transition-colors text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={(e) => handleSubmit(e as any)}
            disabled={isCreating}
            className="px-6 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-white font-medium shadow-sm transition-colors text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {isCreating ? 'Creating Session...' : 'Create Session'}
            {!isCreating && <ArrowRight size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}
